import { BASEURL } from '../Constants';

const getToken = () => sessionStorage.getItem('token');

const pickFirst = (data, keys) => {
  if (!data || typeof data !== 'object') return data;
  const match = keys.find((key) => data[key] !== undefined);
  if (match) return data[match];
  return data;
};

const normalizeReport = (report) => ({
  ...(report || {}),
  findings: Array.isArray(report?.findings) ? report.findings : [],
});

async function request(path, options = {}) {
  const response = await fetch(`${BASEURL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });

  if (response.status === 204) return null;

  const data = await response.json().catch(() => null);
  if (!response.ok || data?.error) {
    throw new Error(data?.message || data?.error || `Request failed with status ${response.status}`);
  }

  return data;
}

export const manualReportsApi = {
  async listReports() {
    const data = await request('/manual_reports');
    const reports = pickFirst(data, ['reports', 'manual_reports', 'manualReports']);
    return Array.isArray(reports) ? reports : [];
  },

  async createReport(payload) {
    const data = await request('/manual_reports', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return pickFirst(data, ['report', 'manual_report', 'manualReport']);
  },

  async getReport(reportId) {
    const data = await request(`/manual_reports/${reportId}`);
    return normalizeReport(pickFirst(data, ['report', 'manual_report', 'manualReport']));
  },

  async updateReport(reportId, payload) {
    const data = await request(`/manual_reports/${reportId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return normalizeReport(pickFirst(data, ['report', 'manual_report', 'manualReport']));
  },

  async deleteReport(reportId) {
    return request(`/manual_reports/${reportId}`, { method: 'DELETE' });
  },

  async createFinding(reportId, payload) {
    const data = await request(`/manual_reports/${reportId}/findings`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return pickFirst(data, ['finding', 'manual_report_finding', 'manualReportFinding']);
  },

  async updateFinding(findingId, payload) {
    const data = await request(`/manual_report_findings/${findingId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return pickFirst(data, ['finding', 'manual_report_finding', 'manualReportFinding']);
  },

  async deleteFinding(findingId) {
    return request(`/manual_report_findings/${findingId}`, { method: 'DELETE' });
  },

  async generateFindingWithInstructions(findingId, payload) {
    const data = await request(`/manual_report_findings/${findingId}/generate`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
    return pickFirst(data, ['finding', 'manual_report_finding', 'manualReportFinding']);
  },

  async generateFindingSection(findingId, section, payload) {
    const data = await request(`/manual_report_findings/${findingId}/generate-${section}`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
    return pickFirst(data, ['finding', 'manual_report_finding', 'manualReportFinding']);
  },

  async generateExecutiveSummary(reportId) {
    const data = await request(`/manual_reports/${reportId}/generate-executive-summary`, {
      method: 'POST',
    });
    return normalizeReport(pickFirst(data, ['report', 'manual_report', 'manualReport']));
  },

  async generateMethodology(reportId) {
    const data = await request(`/manual_reports/${reportId}/generate-methodology`, {
      method: 'POST',
    });
    return normalizeReport(pickFirst(data, ['report', 'manual_report', 'manualReport']));
  },
};
